import {openConfirmDialog, openDialog, showNotification} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import {
  useAddOfferAlternativeMutation,
  useApplyOfferAlternativeMutation,
  useUpdateOfferAlternativeMutation,
  useWithdrawOfferAlternativeMutation,
} from '@dms/api/sales';
import {getCommitFailure, getCommitFailureAlert} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import {getRandomId, type Nullish} from 'shared/web';

import {AlternativeSourceDialog} from '../components/AlternativeSourceDialog';
import {type OfferAlternative, type OfferAlternativeSource} from '../types/OfferAlternative';
import {getAlternativeLabel} from '../utils/getAlternativeLabel';
import {getSourceChangeWarning} from '../utils/getSourceChangeWarning';
import {handleOfferAlternativeError} from '../utils/handleOfferAlternativeError';

const t = (key: string, fallback: string, options: Record<string, string> = {}) =>
  i18n.t(key, {defaultValue: fallback, ...options});

// Applying a configuration commits it like an order does, with the same error codes.
const handleApplyConfigurationError = (error: unknown) => {
  const failure = getCommitFailure(error);
  const alert = getCommitFailureAlert(failure);

  match(failure.kind)
    .with('conflict', 'review-conflict', () =>
      showNotification.warning(
        t(
          'entity.newVehicle.order.conflict',
          'This configuration changed. Review the refreshed details and try again.'
        )
      )
    )
    .with('price-expired', () =>
      showNotification.warning(
        t(
          'entity.offerAlternative.notifications.priceExpired',
          'The configuration price expired. Reprice the configuration, refresh the alternative and try again.'
        )
      )
    )
    .otherwise(() =>
      isNil(alert) ? handleOfferAlternativeError(error) : showNotification.error(alert.message)
    );
};

export function useOfferAlternativeActions(businessCaseId: string) {
  const [addAlternative] = useAddOfferAlternativeMutation();
  const [patchAlternative] = useUpdateOfferAlternativeMutation();
  const [withdrawAlternative] = useWithdrawOfferAlternativeMutation();
  const [applyAlternative, {isLoading: isApplying}] = useApplyOfferAlternativeMutation();

  const openSourceDialog = (
    title: string,
    onSubmit: (source: OfferAlternativeSource) => Promise<unknown>
  ) => {
    // A fresh id per opening, so closing one dialog never closes another.
    const dialogId = `offerAlternativeSource-${getRandomId()}`;
    openDialog(
      <AlternativeSourceDialog
        businessCaseId={businessCaseId}
        dialogId={dialogId}
        onSubmit={onSubmit}
      />,
      {id: dialogId, title, size: 'large'}
    );
  };

  const add = (onAdded?: (alternativeId: string) => void) =>
    openSourceDialog(t('entity.offerAlternative.actions.add', 'Add alternative'), (source) =>
      addAlternative({businessCaseId, addOfferAlternativeRequestBody: source})
        .unwrap()
        .then(({id}) => onAdded?.(id))
        .catch((error: unknown) => {
          handleOfferAlternativeError(error);
          throw error;
        })
    );

  const patchSource = (alternative: OfferAlternative, source: OfferAlternativeSource) =>
    patchAlternative({
      businessCaseId,
      alternativeId: alternative.id,
      updateOfferAlternativeRequestBody: {expectedRevision: alternative.revision, source},
    }).unwrap();

  const openChangeSourceDialog = (alternative: OfferAlternative) =>
    openSourceDialog(
      t('entity.offerAlternative.actions.changeSourceOf', 'Change vehicle · {label}', {
        label: getAlternativeLabel(alternative),
      }),
      (source) =>
        patchSource(alternative, source).catch((error: unknown) => {
          handleOfferAlternativeError(error);
          throw error;
        })
    );

  // The new source drops the negotiated terms, so the seller confirms that first when any exist.
  const changeSource = (alternative: OfferAlternative) => {
    const warning = getSourceChangeWarning(alternative);
    if (isNil(warning)) {
      openChangeSourceDialog(alternative);
      return;
    }
    openConfirmDialog({text: warning, onConfirm: () => openChangeSourceDialog(alternative)});
  };

  // SOURCE_CHANGED: the source changed since it was captured. Sending the alternative's own
  // current source again recaptures its price and specification and keeps the negotiated terms;
  // a discount that no longer fits comes back as a backend error for the seller to adjust.
  const refreshFromSource = (alternative: OfferAlternative) => {
    const source = match(alternative)
      .with({kind: 'CONFIGURATION', configurationId: Pattern.string}, ({configurationId}) => ({
        configurationId,
      }))
      .with({kind: 'STOCK', vehicleId: Pattern.string}, ({vehicleId}) => ({vehicleId}))
      .otherwise(() => null);
    if (isNil(source)) {
      return;
    }
    patchSource(alternative, source)
      .then(() =>
        showNotification.success(
          t('entity.offerAlternative.notifications.refreshed', 'Alternative refreshed')
        )
      )
      .catch(handleOfferAlternativeError);
  };

  const withdraw = (alternative: OfferAlternative) =>
    openConfirmDialog({
      text: t(
        'entity.offerAlternative.withdrawConfirm',
        'Withdraw {label}? The customer will no longer see it, and a choice of it has to be made again.',
        {label: getAlternativeLabel(alternative)}
      ),
      onConfirm: () =>
        withdrawAlternative({businessCaseId, alternativeId: alternative.id})
          .unwrap()
          .catch(handleOfferAlternativeError),
    });

  // `onApplied` runs only after a successful apply (e.g. to continue to checkout).
  const apply = (alternative: OfferAlternative, onApplied?: VoidFunction) =>
    applyAlternative({
      businessCaseId,
      alternativeId: alternative.id,
      applyOfferAlternativeRequestBody: {expectedRevision: alternative.revision},
    })
      .unwrap()
      .then(() => {
        showNotification.success(
          t('entity.offerAlternative.notifications.applied', '{label} is now the deal vehicle', {
            label: getAlternativeLabel(alternative),
          })
        );
        onApplied?.();
      })
      .catch(
        alternative.kind === 'CONFIGURATION'
          ? handleApplyConfigurationError
          : handleOfferAlternativeError
      );

  // "Use for checkout" on another alternative than the customer's current choice overrides that
  // choice, so the seller confirms it first.
  const applyOverChoice = (alternative: OfferAlternative, chosen: OfferAlternative | Nullish) => {
    if (isNil(chosen) || chosen.id === alternative.id) {
      void apply(alternative);
      return;
    }
    openConfirmDialog({
      text: t(
        'entity.offerAlternative.applyOverChoiceConfirm',
        'The customer chose {chosen}. Use {label} for checkout anyway?',
        {chosen: getAlternativeLabel(chosen), label: getAlternativeLabel(alternative)}
      ),
      onConfirm: () => apply(alternative),
    });
  };

  return {add, changeSource, refreshFromSource, withdraw, apply, applyOverChoice, isApplying};
}
